/**
 * FilevineEntityCard - Card wrapper for Filevine entity in linking UI
 *
 * Uses the shared FilevineEntityPreview component for consistent display.
 * Includes a button to open a modal with full entity details.
 */

import { Eye } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type {
  FilevineSectionConfigDataSchema,
  FilevineSectionFieldConfigSchema,
} from '@/services/api/generated/entitySyncService';

import { FilevineEntityDetails, FilevineEntityPreview } from '../shared';
import type { DetailedFilevineCollectionItemSchema } from './types';

interface FilevineEntityCardProps {
  entity: DetailedFilevineCollectionItemSchema;
  fieldConfigs: FilevineSectionFieldConfigSchema[];
  sectionConfigData?: FilevineSectionConfigDataSchema;
  className?: string;
  compact?: boolean;
}

export const FilevineEntityCard: React.FC<FilevineEntityCardProps> = ({
  entity,
  fieldConfigs,
  sectionConfigData,
  className,
  compact = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div
        className={cn(
          'flex rounded-lg border border-zinc-200 bg-white',
          compact ? 'p-2' : 'p-3',
          className,
        )}
      >
        {/* Left: Entity preview aligned to top */}
        <div className="flex-1">
          <FilevineEntityPreview
            item={entity}
            fieldConfigs={fieldConfigs}
            sectionConfigData={sectionConfigData}
          />
        </div>

        {/* Right: Details button aligned to bottom */}
        <div className="flex shrink-0 flex-col justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-zinc-500 hover:text-zinc-700"
            onClick={() => setIsModalOpen(true)}
          >
            <Eye className="mr-1 h-3 w-3" />
            Details
          </Button>
        </div>
      </div>

      {/* Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Entity Details</DialogTitle>
          </DialogHeader>
          <FilevineEntityDetails item={entity} fieldConfigs={fieldConfigs} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FilevineEntityCard;
